import {matchPath, useHistory} from 'react-router';
import {useLocation} from './useLocation';
import {usePageTypePathsMap} from './RouterContext';
import {useCurrentPageType} from './PageTypeContext';
import {useCallback} from 'react';

/**
 * This re-implements `useParams` from react router so that it is working outside of a route
 */
export const useParams = <Params extends Record<string, string>>(): Params => {
  const {pathname} = useLocation();
  const currentPageType = useCurrentPageType();
  const pageTypePathsMap = usePageTypePathsMap();

  if (!currentPageType) {
    return {} as Params;
  }

  if (currentPageType === '404') {
    return {} as Params;
  }

  const patterns = pageTypePathsMap[currentPageType];
  for (const pattern of patterns) {
    const match = matchPath(pathname, {
      path: pattern,
      strict: false,
    });
    if (!match) {
      continue;
    }
    return (match.params ?? {}) as Params;
  }
  return {} as Params;
};

export const useParamsCallback = <
  Params extends Record<string, string>,
>(): (() => Params) => {
  const currentPageType = useCurrentPageType();
  const pageTypePathsMap = usePageTypePathsMap();

  const {location} = useHistory();
  return useCallback(() => {
    const pathname = location.pathname;

    if (!currentPageType) {
      return {} as Params;
    }

    if (currentPageType === '404') {
      return {} as Params;
    }

    const patterns = pageTypePathsMap[currentPageType];
    for (const pattern of patterns) {
      const match = matchPath(pathname, {
        path: pattern,
        strict: false,
      });
      if (!match) {
        continue;
      }
      return (match.params ?? {}) as Params;
    }
    return {} as Params;
  }, [currentPageType, location.pathname, pageTypePathsMap]);
};
